/**
 * ucFmt — чистые форматтеры и календарные хелперы UC-панели «Юникоины»
 * (screen-22, эталон design-references/screen-22-uc.js). УД8: UC-суммы
 * дробные до 2 знаков (fmtUC ≡ эталон js:8), ₽ целые (fmtM ≡ js:9);
 * группировка тысяч — NBSP через общий grp (useNumPadInput). Времена БД —
 * UTC sqlite «YYYY-MM-DD HH:MM:SS» без Z → +5ч (паттерн fmtAuditTs).
 * УД3: даты зарплаты — ближайшие 3 даты грейда 10-е/25-е от РЕАЛЬНОЙ
 * текущей даты (канона зарплатных дат в finance.ts нет — вычисляем).
 */
import { grp, NB } from "../../hooks/useNumPadInput";
import type { UcLedgerKnownType, UcLogSticker } from "../../api/ucTypes";
import { fmtDateApi, todayBusiness } from "../../utils/date";

export { NB };

const UTC5 = 5 * 3_600_000;
const p2 = (n: number): string => String(n).padStart(2, "0");

/** UC-сумма: |n| до 2 знаков, тысячи NBSP только в целой части (js:8). */
export function fmtUC(n: number): string {
  const v = Math.round(Math.abs(n) * 100) / 100;
  const s = String(v);
  const di = s.indexOf(".");
  return di < 0 ? grp(s) : grp(s.slice(0, di)) + s.slice(di);
}

/** ₽-сумма: целая с группировкой (js:9). */
export const fmtM = (n: number): string => grp(String(Math.round(Math.abs(n))));

/** «YYYY-MM-DD…» → «DD.MM» (period_date, лейблы дат). */
export const fmtDD = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;

/** UTC sqlite-время → «DD.MM HH:MM» в UTC+5 (леджер, паттерн EoAuditView). */
export function fmtTm(s: string): string {
  const d = new Date(s.includes("T") ? s : `${s.replace(" ", "T")}Z`);
  if (Number.isNaN(d.getTime())) return "—";
  const b = new Date(d.getTime() + UTC5);
  return `${p2(b.getUTCDate())}.${p2(b.getUTCMonth() + 1)} ${p2(b.getUTCHours())}:${p2(b.getUTCMinutes())}`;
}

/** Первое слово имени (эталон n.split(' ')[0]); null → «—» (LEFT JOIN). */
export const firstWord = (s: string | null | undefined): string =>
  (s || "—").split(" ")[0] || "—";

/** Лейблы тип-чипов леджера (эталон TCH js:158); legacy — сырым именем. */
export const TCH: Record<UcLedgerKnownType, string> = {
  bonus: "bonus",
  penalty: "penalty",
  sticker: "sticker",
  sticker_payout: "выплата",
  roulette: "roulette",
  exchange: "exchange",
};

/** УД3: ближайшие 3 даты зарплатного грейда 10-е/25-е (YYYY-MM-DD, UTC+5). */
export function zpDates(): string[] {
  const t = todayBusiness();
  const out: string[] = [];
  for (let k = 0; k < 3 && out.length < 3; k++) {
    for (const day of [10, 25]) {
      const d = new Date(Date.UTC(t.getUTCFullYear(), t.getUTCMonth() + k, day));
      if (d.getTime() >= t.getTime() && out.length < 3) out.push(fmtDateApi(d));
    }
  }
  return out;
}

/**
 * Остаток жизни наклейки (эталон exp: «4 ч» | «2 дн» | «до 20.08» .long).
 * expires_at null у до-миграционных строк → created_at + hours (COALESCE→1,
 * как в uc-games.ts:137); выплаченные/истёкшие — «—» приглушённо.
 */
export function stickerExp(s: UcLogSticker): { text: string; long: boolean } {
  const raw = s.expires_at;
  const end = raw
    ? Date.parse(raw.includes("T") ? raw : `${raw.replace(" ", "T")}Z`)
    : Date.parse(`${s.created_at.replace(" ", "T")}Z`) + (s.hours ?? 1) * 3_600_000;
  if (Number.isNaN(end)) return { text: "—", long: true };
  const left = end - Date.now();
  if (s.payout_done || left <= 0) return { text: "—", long: true };
  const h = Math.ceil(left / 3_600_000);
  if (h < 24) return { text: `${h}${NB}ч`, long: false };
  const d = Math.ceil(h / 24);
  if (d <= 7) return { text: `${d}${NB}дн`, long: false };
  const b = new Date(end + UTC5);
  return { text: `до ${p2(b.getUTCDate())}.${p2(b.getUTCMonth() + 1)}`, long: true };
}
